import { IBoondock } from '@curbnturf/entities';
import { createReducer, on } from '@ngrx/store';
import { v4 as uuidv4 } from 'uuid';
import * as BoondockActions from './boondock.actions';

/**
 * Interface for the 'Boondock' data used in
 *  - BoondockState, and
 *  - siteReducer
 */
export interface BoondockState {
  boondocks: IBoondock[];
  selected?: string | number;
  error?: string;
  loaded?: boolean;
}

export const initialState: BoondockState = {
  boondocks: [],
  loaded: false,
};

export const boondockReducer = createReducer(
  initialState,
  on(BoondockActions.boondockUpdated, (state, action) => {
      if (!action.boondock) {
        return state;
      }

      const boondock = action.boondock;

      const boondockState = {
        ...state,
        boondocks: [...state.boondocks],
        error: undefined,
      };

      // Find boondock by ID first then by SyncId, Replace with the updated
      const foundIndex = boondockState.boondocks.findIndex(
        (el) => (el.id && boondock.id && el.id === boondock.id) || (el.syncId && boondock.syncId && el.syncId === boondock.syncId),
      );
      if (foundIndex !== -1) {
        boondockState.boondocks.splice(foundIndex, 1, boondock);
      } else {
        boondockState.boondocks.push(boondock);
      }

      return boondockState;
  }),
  on(BoondockActions.boondockRestored, (state, action) => {
    if (!action.boondock) {
      return state;
    }

    const boondock = action.boondock;

    const boondockState = {
      ...state,
      boondocks: [...state.boondocks],
      error: undefined,
    };

    // Find boondock by ID first then by SyncId, Replace with the updated
    const foundIndex = boondockState.boondocks.findIndex(
      (el) => (el.id && boondock.id && el.id === boondock.id) || (el.syncId && boondock.syncId && el.syncId === boondock.syncId),
    );
    if (foundIndex !== -1) {
      boondockState.boondocks.splice(foundIndex, 1, boondock);
    } else {
      boondockState.boondocks.push(boondock);
    }

    return boondockState;
  }),
  on(BoondockActions.boondockLoaded, (state, action) => {
    if (!action.boondock) {
      return state;
    }

    const boondock = action.boondock;

    const boondockState = {
      ...state,
      boondocks: [...state.boondocks],
      error: undefined,
    };

    // Find boondock by ID first then by SyncId, Replace with the updated
    const foundIndex = boondockState.boondocks.findIndex(
      (el) => (el.id && boondock.id && el.id === boondock.id) || (el.syncId && boondock.syncId && el.syncId === boondock.syncId),
    );
    if (foundIndex !== -1) {
      boondockState.boondocks.splice(foundIndex, 1, boondock);
    } else {
      boondockState.boondocks.push(boondock);
    }

    return boondockState;
  }),

  on(BoondockActions.boondockImagesUpdated, (state, action) => {
    if (!action.photos || (!action.id && !action.syncId)) {
      return state;
    }

    const id = action.id;
    const syncId = action.syncId;
    const photos = action.photos;

    const boondockState = {
      ...state,
      boondocks: [...state.boondocks],
      error: undefined,
    };

    // Find boondock by ID first then by SyncId, Replace with the updated
    const foundIndex = boondockState.boondocks.findIndex(
      (el) => (el.id && id && el.id === id) || (el.syncId && syncId && el.syncId === syncId),
    );
    if (foundIndex !== -1) {
      const boondock: IBoondock = {
        ...boondockState.boondocks[foundIndex],
      };

      boondock.photos = photos;
      boondockState.boondocks.splice(foundIndex, 1, boondock);
    }

    return boondockState;
  }),

  on(BoondockActions.boondocksLoaded, (state, action) => {
    const boondocks: IBoondock[] = [...state.boondocks];
    action.boondocks?.forEach((boondock) => {
      if (boondock && (boondock.id || boondock.syncId)) {
        const foundIndex = boondocks.findIndex(
          (el) => (el.id && boondock.id && el.id === boondock.id) || (el.syncId && boondock.syncId && el.syncId === boondock.syncId),
        );
        if (foundIndex === -1) {
          boondocks.push(boondock);
        } else {
          boondocks.splice(foundIndex, 1, boondock);
        }
      }
    });

    return {
      ...state,
      boondocks,
      error: undefined,
      loaded: true,
    };
  }),

  on(BoondockActions.clearBoondocks, (state) => {
    return { ...state, boondocks: [], error: undefined };
  }),
  on(BoondockActions.boondockRemoved, (state, action) => {
    const boondock = action.boondock;

    const boondockState = {
      ...state,
      boondocks: [...state.boondocks],
      error: undefined,
    };

    // Find boondock by ID first then by SyncId, Replace with the updated
    const foundIndex = boondockState.boondocks.findIndex(
      (el) => (el.id && boondock.id && el.id === boondock.id) || (el.syncId && boondock.syncId && el.syncId === boondock.syncId),
    );
    if (foundIndex !== -1) {
      boondockState.boondocks.splice(foundIndex, 1);
    }

    return boondockState;
  }),

  on(BoondockActions.boondockEnabled, (state, action) => {
    const id = action.boondockId;

    const boondockState = {
      ...state,
      boondocks: [...state.boondocks],
      error: undefined,
    };

    // Find boondock by ID first then by SyncId
    const foundIndex = boondockState.boondocks.findIndex(
      (el) => (el.id && id && el.id === id) || (el.syncId && id && el.syncId === id),
    );
    if (foundIndex !== -1) {
      const boondock: IBoondock = { ...boondockState.boondocks[foundIndex], available: true };
      boondockState.boondocks.splice(foundIndex, 1, boondock);
    }

    return boondockState;
  }),
  on(BoondockActions.boondockDisabled, (state, action) => {
    const id = action.boondockId;

    const boondockState = {
      ...state,
      boondocks: [...state.boondocks],
      error: undefined,
    };

    // Find boondock by ID first then by SyncId
    const foundIndex = boondockState.boondocks.findIndex(
      (el) => (el.id && id && el.id === id) || (el.syncId && id && el.syncId === id),
    );
    if (foundIndex !== -1) {
      const boondock: IBoondock = { ...boondockState.boondocks[foundIndex], available: false };
      boondockState.boondocks.splice(foundIndex, 1, boondock);
    }

    return boondockState;
  }),
  on(BoondockActions.selectBoondockByBoondock, (state, action) => {
    if (!action.boondock || (!action.boondock.id && !action.boondock.syncId)) {
      return {
        ...state,
        selected: undefined,
        error: undefined,
      };
    }

    const id: number | string = action.boondock.id ?? action.boondock.syncId ?? uuidv4();
    return {
      ...state,
      selected: id,
      error: undefined,
    };
  }),
  on(BoondockActions.boondockSelected, (state, action) => {
    if (!action.boondock || (!action.boondock.id && !action.boondock.syncId)) {
      return {
        ...state,
        selected: undefined,
        error: undefined,
      };
    }

    const id: number | string = action.boondock.id ?? action.boondock.syncId ?? uuidv4();
    return {
      ...state,
      selected: id,
      error: undefined,
    };
  }),
  on(BoondockActions.updateBoondockFailed, (state, action) => {
    return {
      ...state,
      error: action.error,
    };
  }),
  on(BoondockActions.listingFavorFailed, (state, action) => {
    return {
      ...state,
      error: action.error,
    };
  }),
  on(BoondockActions.listingFavorCompleted, (state) => {
    return {
      ...state,
      error: undefined,
    };
  }),
);
